import React from "react";
import { NavLink } from "react-router-dom";
import "../styles/Training.css";

export default function Training() {
  const trainingCapabilities = [
    {
      number: "01",
      stage: "Workforce Capability",
      title: "Customized Organizational Training",
      description:
        "Programs are designed around the organization’s risk profile, workforce, systems, and desired outcomes.",
      details:
        "Content may be adapted for executives, supervisors, practitioners, workers, joint committees, or cross-functional teams.",
      applications: [
        "Organization-specific risks",
        "Role-based learning",
        "Practical workplace scenarios",
        "Cross-functional capability",
      ],
      cta: "Discuss Customized Training",
      link: "/contact",
    },
    {
      number: "02",
      stage: "Leadership Capability",
      title: "Leadership and Governance Learning",
      description:
        "Build executive and operational leadership capability to understand safety as a governance, risk, culture, and performance responsibility.",
      details:
        "Learning connects leadership expectations with accountability, oversight, decision-making, organizational culture, and operational performance—not only compliance.",
      applications: [
        "Executive accountability",
        "Governance and oversight",
        "Risk-informed decisions",
        "Safety leadership culture",
      ],
      cta: "Explore Leadership Learning",
      link: "/contact",
    },
    {
      number: "03",
      stage: "Applied Learning Innovation",
      title: "Immersive and Simulation-Based Learning",
      description:
        "Explore scenario-based, simulation, VR, AR, and other immersive methods where they add credible learning value.",
      details:
        "Potential applications include hazard recognition, decision-making, scenario practice, and learning transfer in complex or high-risk environments.",
      applications: [
        "Hazard recognition",
        "Decision-making practice",
        "Complex scenario simulation",
        "Learning transfer",
      ],
      cta: "Explore Immersive Learning",
      link: "/contact",
    },
  ];

  const learningFactors = [
    {
      title: "The Work",
      description:
        "Learning reflects the actual tasks, environments, and operational realities people experience.",
    },
    {
      title: "The Risk",
      description:
        "Content focuses on the hazards, decisions, and controls that matter most.",
    },
    {
      title: "The Learner",
      description:
        "Programs consider role, experience, language, knowledge, and confidence.",
    },
    {
      title: "The Decision",
      description:
        "Learning prepares people to recognize situations and make better choices.",
    },
  ];

  return (
    <main className="training-page">
      {/* PAGE HERO */}
      <section
        className="training-hero"
        aria-labelledby="training-hero-title"
      >
        <div className="training-hero-background" aria-hidden="true">
          <span className="training-hero-glow training-hero-glow-left"></span>
          <span className="training-hero-glow training-hero-glow-right"></span>

          <span className="training-hero-orbit training-hero-orbit-one"></span>
          <span className="training-hero-orbit training-hero-orbit-two"></span>

          <span className="training-hero-line training-hero-line-one"></span>
          <span className="training-hero-line training-hero-line-two"></span>
        </div>

        <div className="training-hero-container">
          <p className="training-kicker">Customized Learning</p>

          <h1 id="training-hero-title" className="training-hero-title">
            Learning Designed for
            <span> Real Work.</span>
          </h1>

          <p className="training-hero-description">
            Customized learning that strengthens knowledge, judgement,
            engagement, and the ability to apply learning where it matters
            most.
          </p>

          <NavLink
            to="/contact"
            className="training-primary-button"
          >
            <span>Discuss a Training Need</span>

            <span
              className="training-button-arrow"
              aria-hidden="true"
            >
              ↗
            </span>
          </NavLink>

          <div
            className="training-hero-path"
            aria-label="GESI learning progression"
          >
            <span>Knowledge</span>
            <span aria-hidden="true">→</span>
            <span>Judgement</span>
            <span aria-hidden="true">→</span>
            <span>Application</span>
            <span aria-hidden="true">→</span>
            <span>Capability</span>
          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section
        className="training-introduction"
        aria-labelledby="training-introduction-title"
      >
        <div className="training-introduction-container">
          <div className="training-introduction-heading">
            <p className="training-section-kicker">
              Learning That Transfers
            </p>

            <h2
              id="training-introduction-title"
              className="training-section-title"
            >
              From Course Completion to
              <span> Workforce Capability</span>
            </h2>
          </div>

          <div className="training-introduction-copy">
            <p>
              Effective learning must reflect the work, risk, learner, and
              decisions people are expected to make.
            </p>

            <p>
              GESI designs customized learning that connects safety
              requirements with practical scenarios, behavioral insight,
              leadership expectations, and organizational context.
            </p>
          </div>

          <div className="training-learning-grid">
            {learningFactors.map((factor, index) => (
              <article
                className="training-learning-factor"
                key={factor.title}
              >
                <span
                  className="training-learning-number"
                  aria-hidden="true"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>

                <h3>{factor.title}</h3>
                <p>{factor.description}</p>

                <span
                  className="training-learning-line"
                  aria-hidden="true"
                ></span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section
        className="training-capabilities"
        aria-labelledby="training-capabilities-title"
      >
        <div
          className="training-capabilities-background"
          aria-hidden="true"
        ></div>

        <div className="training-capabilities-container">
          <header className="training-capabilities-heading">
            <p className="training-section-kicker">
              Learning Capabilities
            </p>

            <h2
              id="training-capabilities-title"
              className="training-section-title"
            >
              Designed Around the Learner,
              <span> Built for Application</span>
            </h2>

            <p className="training-capabilities-intro">
              GESI develops learning experiences that align people, risk,
              leadership expectations, and the organizational environment in
              which knowledge must be applied.
            </p>
          </header>

          <div className="training-capability-list">
            {trainingCapabilities.map((capability) => (
              <article
                className="training-capability"
                key={capability.title}
              >
                <div className="training-capability-marker">
                  <span>{capability.number}</span>
                </div>

                <div className="training-capability-card">
                  <div className="training-capability-top">
                    <div>
                      <p className="training-capability-stage">
                        {capability.stage}
                      </p>

                      <h3>{capability.title}</h3>
                    </div>

                    <span
                      className="training-capability-index"
                      aria-hidden="true"
                    >
                      {capability.number}
                    </span>
                  </div>

                  <div className="training-capability-body">
                    <div className="training-capability-copy">
                      <p className="training-capability-lead">
                        {capability.description}
                      </p>

                      <p>{capability.details}</p>

                      <div className="training-capability-outcome">
                        <span aria-hidden="true">✓</span>

                        <p>
                          Learning is structured to strengthen practical
                          application, not simply course completion.
                        </p>
                      </div>
                    </div>

                    <div className="training-capability-side">
                      <p className="training-capability-label">
                        Potential applications
                      </p>

                      <ul className="training-capability-applications">
                        {capability.applications.map((application) => (
                          <li key={application}>
                            <span aria-hidden="true"></span>
                            {application}
                          </li>
                        ))}
                      </ul>

                      <NavLink
                        to={capability.link}
                        className="training-capability-button"
                      >
                        <span>{capability.cta}</span>

                        <span
                          className="training-capability-button-arrow"
                          aria-hidden="true"
                        >
                          ↗
                        </span>
                      </NavLink>
                    </div>
                  </div>

                  <span
                    className="training-capability-bottom-line"
                    aria-hidden="true"
                  ></span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* LEARNING MODEL */}
      <section
        className="training-model"
        aria-labelledby="training-model-title"
      >
        <div className="training-model-background" aria-hidden="true">
          <span className="training-model-glow"></span>
        </div>

        <div className="training-model-container">
          <div className="training-model-content">
            <p className="training-section-kicker">
              The Learning Objective
            </p>

            <h2
              id="training-model-title"
              className="training-section-title"
            >
              Learning That Changes
              <span> What Happens at Work</span>
            </h2>

            <p className="training-model-description">
              Effective learning connects information with judgement, practice,
              confidence, and the conditions required for people to apply what
              they know.
            </p>
          </div>

          <div
            className="training-model-flow"
            aria-label="Learning capability model"
          >
            <div className="training-model-step">
              <span>01</span>
              <strong>Understand</strong>
              <p>Build relevant knowledge.</p>
            </div>

            <span className="training-model-arrow" aria-hidden="true">
              →
            </span>

            <div className="training-model-step">
              <span>02</span>
              <strong>Practice</strong>
              <p>Apply judgement in context.</p>
            </div>

            <span className="training-model-arrow" aria-hidden="true">
              →
            </span>

            <div className="training-model-step">
              <span>03</span>
              <strong>Transfer</strong>
              <p>Use learning in real work.</p>
            </div>

            <span className="training-model-arrow" aria-hidden="true">
              →
            </span>

            <div className="training-model-step">
              <span>04</span>
              <strong>Strengthen</strong>
              <p>Improve capability over time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="training-final-cta"
        aria-labelledby="training-final-title"
      >
        <div className="training-final-background" aria-hidden="true">
          <span className="training-final-glow"></span>
          <span className="training-final-orbit"></span>
        </div>

        <div className="training-final-container">
          <p className="training-section-kicker">
            Build Practical Capability
          </p>

          <h2
            id="training-final-title"
            className="training-final-title"
          >
            Design Learning Around
            <span> the Work That Matters.</span>
          </h2>

          <p className="training-final-description">
            Share the capability, workforce challenge, or learning outcome your
            organization needs to strengthen. GESI will help shape a practical,
            context-specific approach.
          </p>

          <NavLink
            to="/contact"
            className="training-primary-button"
          >
            <span>Discuss a Training Need</span>

            <span
              className="training-button-arrow"
              aria-hidden="true"
            >
              ↗
            </span>
          </NavLink>
        </div>
      </section>
    </main>
  );
}